import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Backdrop,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import InsightsIcon from '@mui/icons-material/Insights';
import RadarIcon from '@mui/icons-material/Radar';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TimelineIcon from '@mui/icons-material/Timeline';
import SpeedIcon from '@mui/icons-material/Speed';
import ApiIcon from '@mui/icons-material/Api';
import BugReportIcon from '@mui/icons-material/BugReport';
import { useNavigate, useParams } from 'react-router-dom';

import Nav from '../nav';
import { BASEURL, PRIMARYCOLOR, COMPANY } from '../../../Constants';
import WhiteLabelForm from '../../../components/whiteLabelForm';
import {
  buildApiReportPdfData,
  SEVERITY_COLORS,
} from './apiReportPdfHelpers';

import './ReportsPage.css';

const DEFAULT_PRIMARY = PRIMARYCOLOR || '#005CC5';
const DEFAULT_COMPANY = COMPANY || 'StealthNet AI';
const DEFAULT_CLIENT = '';

const METHOD_COLORS = {
  GET: '#2563eb',
  POST: '#0f766e',
  PUT: '#7c3aed',
  DELETE: '#dc2626',
  PATCH: '#ea580c',
};

const downloadBlob = (blob, filename) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

const ExecutiveReportAPIPentest = ({ minimal = false, primary = PRIMARYCOLOR, company = COMPANY, client, reportData = null }) => {
  const { pentest_id } = useParams();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [exploits, setExploits] = useState([]);
  const [executiveSummary, setExecutiveSummary] = useState('');
  const [loading, setLoading] = useState(!reportData);
  const [downloading, setDownloading] = useState(false);

  const [primaryColor, setPrimaryColor] = useState(primary || DEFAULT_PRIMARY);
  const [companyName, setCompanyName] = useState(company || DEFAULT_COMPANY);
  const [clientName, setClientName] = useState(client || DEFAULT_CLIENT);

  const applyWhiteLabel = ({ primary: nextPrimary, company: nextCompany, client: nextClient }) => {
    if (nextPrimary !== undefined) setPrimaryColor(nextPrimary);
    if (nextCompany !== undefined) setCompanyName(nextCompany);
    if (nextClient !== undefined) setClientName(nextClient);
  };

  useEffect(() => {
    setPrimaryColor(primary || DEFAULT_PRIMARY);
  }, [primary]);

  useEffect(() => {
    setCompanyName(company || DEFAULT_COMPANY);
  }, [company]);

  useEffect(() => {
    if (client !== undefined) {
      setClientName(client);
    }
  }, [client]);

  useEffect(() => {
    if (reportData) {
      setLoading(false);
      return;
    }

    const token = sessionStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      fetch(`${BASEURL}/pentests/api/stats?pentest_id=${pentest_id}`, { headers }).then((response) => response.json()),
      fetch(`${BASEURL}/pentests/api/exploits?pentest_id=${pentest_id}`, { headers }).then((response) => response.json()),
      fetch(`${BASEURL}/pentest/report/executive?pentest_id=${pentest_id}`, { headers }).then((response) => response.json()),
    ])
      .then(([statsRes, exploitsRes, summaryRes]) => {
        if ([statsRes, exploitsRes, summaryRes].some((result) => result?.message === 'Invalid token')) {
          navigate('/');
          return;
        }

        setStats(statsRes?.stats || statsRes || null);
        setExploits(exploitsRes?.exploits || []);
        setExecutiveSummary(summaryRes?.report || '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [navigate, pentest_id, reportData]);

  const report = useMemo(() => {
    if (reportData) return reportData;
    if (!stats) return null;

    return buildApiReportPdfData({
      pentestId: pentest_id,
      primaryColor,
      companyName,
      clientName,
      stats,
      exploits,
      executiveSummary,
    });
  }, [clientName, companyName, executiveSummary, exploits, pentest_id, primaryColor, reportData, stats]);

  const handleDownloadPDF = async () => {
    if (!report) return;

    setDownloading(true);
    try {
      const [{ pdf }, { ApiExecutivePdfDocument }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('./apiReportPdf'),
      ]);

      const blob = await pdf(<ApiExecutivePdfDocument report={report} />).toBlob();
      downloadBlob(blob, `Executive_Report_API_${pentest_id}.pdf`);
    } finally {
      setDownloading(false);
    }
  };

  if (loading || !report) {
    return (
      <Box sx={{ display: 'flex', height: minimal ? 'auto' : '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (minimal) {
    return (
      <Box className="report-surface" sx={{ maxWidth: '8.5in', mx: 'auto', py: 4, px: 3 }}>
        <ExecutiveBody report={report} />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <Nav />
        <Button
          variant="contained"
          onClick={handleDownloadPDF}
          sx={{
            position: 'absolute',
            top: 8,
            right: 16,
            backgroundColor: DEFAULT_PRIMARY,
            '&:hover': {
              backgroundColor: darken(DEFAULT_PRIMARY, 0.12),
            },
          }}
        >
          Download PDF
        </Button>
      </Box>

      <Backdrop open={downloading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Card className="report-config-card" sx={{ width: '100%', maxWidth: '8.5in', mb: 2, boxShadow: 'none', border: '1px solid rgba(0,0,0,0.08)' }}>
          <WhiteLabelForm
            defaultPrimary={DEFAULT_PRIMARY}
            defaultCompany={DEFAULT_COMPANY}
            defaultClient={DEFAULT_CLIENT}
            onApply={applyWhiteLabel}
            showCompany
            showClient
            title="White Label"
          />
        </Card>
      </Box>

      <Box className="report-platform-shell" sx={{ minHeight: '100vh', pt: 2, pb: 6, px: 2 }}>
        <Box className="pdf-page exec-page" sx={{ display: 'flex', justifyContent: 'center' }} data-report-section="executive">
          <Card className="report-page-card report-page-card--standard exec-wrapper allow-split" sx={{ width: '100%', maxWidth: '8.5in' }}>
            <Box
              sx={{
                width: '100%',
                boxSizing: 'border-box',
                backgroundColor: '#ffffff',
                px: '0.72in',
                py: '0.68in',
              }}
            >
              <ExecutiveBody report={report} />
            </Box>
          </Card>
        </Box>
      </Box>
    </>
  );
};

const Header = ({ report }) => {
  const { brand } = report;

  return (
    <Card
      className="avoid-break"
      sx={{
        p: 2.5,
        mb: 2.5,
        boxShadow: 0,
        border: `1px solid ${alpha(brand.ink, 0.08)}`,
        bgcolor: '#fff',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 6,
          background: `linear-gradient(90deg, ${brand.primaryDark}, ${brand.primaryMid} 60%, ${brand.primaryLight})`,
        }}
      />

      <Grid container spacing={1.5} alignItems="center">
        <Grid item xs={12} md={7}>
          <Stack direction="row" spacing={1.6} alignItems="center">
            <Avatar
              variant="rounded"
              sx={{
                width: 52,
                height: 52,
                bgcolor: alpha(brand.primary, 0.12),
                color: brand.primaryMid,
                border: `1px solid ${alpha(brand.primary, 0.28)}`,
                fontWeight: 900,
              }}
            >
              <SecurityIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.2, color: brand.ink }}>
                Executive Security Assessment
              </Typography>
              <Typography variant="body2" sx={{ color: alpha(brand.ink, 0.7) }}>
                {report.organizationName} • {report.testingType} • ID #{report.pentestId}
              </Typography>
              <Typography variant="body2" sx={{ color: alpha(brand.ink, 0.7) }}>
                {report.reportDate}
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ mt: 0.8, flexWrap: 'wrap' }}>
                <Chip
                  icon={<VerifiedUserIcon />}
                  size="small"
                  label={report.companyName}
                  sx={{
                    bgcolor: alpha(brand.primary, 0.08),
                    color: brand.primaryMid,
                    border: `1px solid ${alpha(brand.primary, 0.2)}`,
                    fontWeight: 800,
                    borderRadius: '999px',
                  }}
                />
                <Chip
                  icon={<InsightsIcon />}
                  size="small"
                  label="OWASP API Top 10"
                  sx={{
                    bgcolor: alpha(brand.primaryLight, 0.12),
                    color: brand.primaryMid,
                    border: `1px solid ${alpha(brand.primary, 0.2)}`,
                    fontWeight: 700,
                    borderRadius: '999px',
                  }}
                />
                <Chip
                  icon={<ApiIcon />}
                  size="small"
                  label="API Surface"
                  sx={{
                    bgcolor: alpha(brand.primaryDark, 0.12),
                    color: brand.primaryMid,
                    border: `1px solid ${alpha(brand.primary, 0.2)}`,
                    fontWeight: 700,
                    borderRadius: '999px',
                  }}
                />
              </Stack>
            </Box>
          </Stack>
        </Grid>

        <Grid item xs={12} md={5}>
          <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Button
              variant="contained"
              sx={{ bgcolor: report.gradeColor, color: '#fff', fontWeight: 900, px: 1.25, borderRadius: '12px' }}
              startIcon={<RadarIcon />}
            >
              GRADE: {report.grade}
            </Button>
            <Typography variant="caption" sx={{ display: 'block', color: alpha(brand.ink, 0.7), mt: 0.5 }}>
              Risk Score: <strong>{report.riskScore ?? '-'}</strong>
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: alpha(brand.ink, 0.7) }}>
              {report.gradeMessage}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
};

const Kpi = ({ label, value, hint, footnote, icon, accent, brand }) => {
  const tone = accent || brand.primary;

  return (
    <Card
      className="avoid-break kpi-card"
      sx={{
        p: 1.4,
        minHeight: 118,
        border: `1px solid ${alpha(brand.ink, 0.08)}`,
        boxShadow: 0,
        background: `linear-gradient(180deg, ${alpha(tone, 0.12)} 0%, rgba(255,255,255,0.98) 58%)`,
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 5,
          background: `linear-gradient(90deg, ${alpha(tone, 0.96)}, ${alpha(tone, 0.6)})`,
        }}
      />

      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="overline" sx={{ color: alpha(brand.ink, 0.62), fontWeight: 900, letterSpacing: 1.25, lineHeight: 1 }}>
            {label}
          </Typography>
          <Typography sx={{ mt: 0.55, fontSize: '1.7rem', fontWeight: 900, lineHeight: 1, color: brand.ink }}>
            {value}
          </Typography>
        </Box>
        <Avatar
          sx={{
            width: 34,
            height: 34,
            bgcolor: alpha(tone, 0.12),
            color: tone,
            border: `1px solid ${alpha(tone, 0.22)}`,
            boxShadow: `0 6px 18px ${alpha(tone, 0.16)}`,
          }}
        >
          {icon}
        </Avatar>
      </Stack>

      <Divider sx={{ my: 0.85, borderColor: alpha(tone, 0.12) }} />

      <Typography variant="body2" sx={{ color: alpha(brand.ink, 0.84), lineHeight: 1.32, fontWeight: 600, fontSize: '0.82rem' }}>
        {hint}
      </Typography>
      {footnote ? (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.45, color: alpha(brand.ink, 0.58), lineHeight: 1.2, fontSize: '0.68rem' }}>
          {footnote}
        </Typography>
      ) : null}
    </Card>
  );
};

const SectionTitle = ({ children, brand }) => (
  <Box sx={{ mb: 0.85 }}>
    <Typography variant="h6" sx={{ fontWeight: 900, color: brand.ink }}>
      {children}
    </Typography>
    <Box
      sx={{
        height: 3,
        width: 156,
        borderRadius: 2,
        mt: 0.55,
        background: `linear-gradient(90deg, ${brand.primaryDark}, ${brand.primaryMid} 60%, ${brand.primaryLight})`,
      }}
    />
  </Box>
);

const SeverityProfilePanel = ({ report }) => (
  <Card className="avoid-break" sx={{ p: 2, border: `1px solid ${alpha(report.brand.ink, 0.08)}`, boxShadow: 0, height: '100%' }}>
    <SectionTitle brand={report.brand}>Risk Profile (by severity)</SectionTitle>

    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.25 }}>
      <Chip
        size="small"
        label={`Critical + High: ${report.highImpactCount}`}
        sx={{
          bgcolor: alpha(SEVERITY_COLORS.High, 0.12),
          color: SEVERITY_COLORS.High,
          border: `1px solid ${alpha(SEVERITY_COLORS.High, 0.22)}`,
          fontWeight: 800,
        }}
      />
      <Chip
        size="small"
        label={`${report.highImpactShare}% of validated findings`}
        sx={{
          bgcolor: alpha(report.brand.primary, 0.08),
          color: report.brand.primaryMid,
          border: `1px solid ${alpha(report.brand.primary, 0.18)}`,
          fontWeight: 700,
        }}
      />
    </Stack>

    <Box
      sx={{
        p: 1.45,
        borderRadius: 3,
        bgcolor: '#f8fafc',
        border: `1px solid ${alpha(report.brand.ink, 0.06)}`,
      }}
    >
      <Box sx={{ position: 'relative', height: 212 }}>
        {[0.25, 0.5, 0.75, 1].map((ratio) => (
          <Box
            key={ratio}
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: `${ratio * 100}%`,
              borderTop: `1px dashed ${alpha(report.brand.ink, 0.1)}`,
            }}
          />
        ))}

        <Stack direction="row" spacing={1.15} alignItems="flex-end" sx={{ position: 'relative', zIndex: 1, height: '100%' }}>
          {report.severityStats.map((item) => {
            const height = item.value
              ? `${Math.max(14, (item.value / report.maxSeverityCount) * 100)}%`
              : '8px';

            return (
              <Box
                key={item.label}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
              >
                <Typography variant="caption" sx={{ mb: 0.65, fontWeight: 900, color: item.value ? report.brand.ink : alpha(report.brand.ink, 0.42) }}>
                  {item.value}
                </Typography>

                <Box
                  sx={{
                    width: '100%',
                    maxWidth: 50,
                    height,
                    borderRadius: '16px 16px 10px 10px',
                    background: `linear-gradient(180deg, ${alpha(item.color, 0.78)} 0%, ${item.color} 100%)`,
                    border: `1px solid ${alpha(item.color, 0.2)}`,
                    boxShadow: `0 12px 24px ${alpha(item.color, 0.18)}`,
                  }}
                />

                <Typography variant="caption" sx={{ mt: 0.65, fontWeight: 800, color: alpha(report.brand.ink, 0.78) }}>
                  {item.label}
                </Typography>
                <Typography variant="caption" sx={{ color: alpha(report.brand.ink, 0.5) }}>
                  {item.share}%
                </Typography>
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Box>
  </Card>
);

const HttpMethodsPanel = ({ report }) => {
  const visibleStats = report.methodStats.filter((item) => item.value > 0);

  return (
    <Card className="avoid-break" sx={{ p: 1.8, border: `1px solid ${alpha(report.brand.ink, 0.08)}`, boxShadow: 0, height: '100%' }}>
      <SectionTitle brand={report.brand}>HTTP Method Distribution</SectionTitle>

      <Box
        sx={{
          p: 1.2,
          borderRadius: 3,
          bgcolor: '#f8fafc',
          border: `1px solid ${alpha(report.brand.ink, 0.06)}`,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mb: 0.95 }}>
          <Box>
            <Typography variant="caption" sx={{ color: alpha(report.brand.ink, 0.62), fontWeight: 800, letterSpacing: 0.5 }}>
              OBSERVED METHODS
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1, color: report.brand.ink, mt: 0.2 }}>
              {report.activeMethodCount}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{ color: alpha(report.brand.ink, 0.62), fontWeight: 800, letterSpacing: 0.5 }}>
              METHOD OBSERVATIONS
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 900, color: report.brand.primaryMid, lineHeight: 1.1, mt: 0.2 }}>
              {report.totalMethodObservations}
            </Typography>
            <Typography variant="caption" sx={{ color: alpha(report.brand.ink, 0.58) }}>
              across inventoried endpoints
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction="row"
          sx={{
            height: 14,
            borderRadius: 999,
            overflow: 'hidden',
            bgcolor: alpha(report.brand.ink, 0.07),
            mb: 1,
          }}
        >
          {(visibleStats.length ? visibleStats : report.methodStats.slice(0, 1)).map((item) => (
            <Box
              key={item.label}
              sx={{
                flex: visibleStats.length ? Math.max(item.value, 1) : 1,
                bgcolor: visibleStats.length ? (METHOD_COLORS[item.label] || report.brand.primaryMid) : alpha(report.brand.ink, 0.14),
              }}
            />
          ))}
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 0.75,
          }}
        >
          {report.methodStats.map((item) => {
            const tone = METHOD_COLORS[item.label] || report.brand.primaryMid;

            return (
              <Box
                key={item.label}
                sx={{
                  p: 0.72,
                  borderRadius: 2,
                  bgcolor: alpha(tone, 0.06),
                  border: `1px solid ${alpha(tone, 0.14)}`,
                  minWidth: 0,
                }}
              >
                <Stack spacing={0.15} alignItems="center" sx={{ textAlign: 'center' }}>
                  <Stack direction="row" spacing={0.6} alignItems="center" justifyContent="center" sx={{ minWidth: 0 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tone, flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ fontWeight: 800, color: alpha(report.brand.ink, 0.76), whiteSpace: 'nowrap' }}>
                      {item.label}
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: report.brand.ink, lineHeight: 1.1 }}>
                    {item.value}
                  </Typography>
                  <Typography variant="caption" sx={{ color: alpha(report.brand.ink, 0.48), lineHeight: 1 }}>
                    {item.share}% of observations
                  </Typography>
                </Stack>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Card>
  );
};

const TopFindingRow = ({ finding, index, brand }) => {
  const tone = SEVERITY_COLORS[finding.severity] || brand.primaryMid;

  return (
    <Box
      sx={{
        p: 1.45,
        borderRadius: 2.5,
        border: `1px solid ${alpha(tone, 0.16)}`,
        background: `linear-gradient(180deg, ${alpha(tone, 0.07)} 0%, rgba(255,255,255,0.98) 55%)`,
      }}
    >
      <Stack direction="row" spacing={1.35} alignItems="flex-start">
        <Avatar
          sx={{
            width: 34,
            height: 34,
            bgcolor: alpha(tone, 0.12),
            color: tone,
            border: `1px solid ${alpha(tone, 0.18)}`,
            fontWeight: 900,
            fontSize: 15,
          }}
        >
          {index + 1}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" sx={{ fontWeight: 800, color: brand.ink, lineHeight: 1.35 }}>
            {finding.title}
          </Typography>

          <Stack direction="row" spacing={0.9} useFlexGap flexWrap="wrap" sx={{ mt: 0.85, rowGap: 0.75 }}>
            {finding.asset ? (
              <Chip
                size="small"
                label={finding.asset}
                sx={{
                  maxWidth: 240,
                  bgcolor: alpha(brand.primary, 0.08),
                  color: brand.primaryMid,
                  border: `1px solid ${alpha(brand.primary, 0.16)}`,
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
            ) : null}
            <Typography variant="caption" sx={{ color: alpha(brand.ink, 0.58), fontWeight: 700, alignSelf: 'center' }}>
              Assessment-confirmed finding
            </Typography>
          </Stack>
        </Box>

        <Chip
          size="small"
          label={finding.severity}
          sx={{
            bgcolor: tone,
            color: '#fff',
            fontWeight: 800,
            borderRadius: '999px',
            alignSelf: 'center',
          }}
        />
      </Stack>
    </Box>
  );
};

const ExecutiveBody = ({ report }) => (
  <>
    <Header report={report} />

    <Grid container spacing={1.5} sx={{ mb: 2.1 }}>
      <Grid item xs={12} md={4}>
        <Kpi
          label="Endpoints"
          value={report.totalEndpoints}
          hint="Reachable pages, routes, and endpoints inventoried during review."
          footnote="Surface mapping informed test coverage and prioritization."
          icon={<TimelineIcon fontSize="small" />}
          accent={report.brand.primaryMid}
          brand={report.brand}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Kpi
          label="Findings"
          value={report.totalFindings}
          hint="Validated API security issues confirmed during testing."
          footnote={`${report.highImpactCount} critical/high issues currently drive material risk.`}
          icon={<WarningAmberIcon fontSize="small" />}
          accent={SEVERITY_COLORS.High}
          brand={report.brand}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Kpi
          label="Risk Score"
          value={report.riskScore ?? '-'}
          hint="Overall API risk posture derived from validated findings."
          footnote={`Current grade: ${report.grade}`}
          icon={<SpeedIcon fontSize="small" />}
          accent={report.gradeColor}
          brand={report.brand}
        />
      </Grid>
    </Grid>

    <Grid container spacing={2} sx={{ mb: 2.4 }}>
      <Grid item xs={12} md={7}>
        <SeverityProfilePanel report={report} />
      </Grid>
      <Grid item xs={12} md={5}>
        <HttpMethodsPanel report={report} />
      </Grid>
    </Grid>

    {report.topFindings.length > 0 && (
      <Card
        className="avoid-break"
        sx={{
          p: 2.5,
          mb: 3,
          border: `1px solid ${alpha(report.brand.ink, 0.08)}`,
          boxShadow: 0,
        }}
      >
        <SectionTitle brand={report.brand}>Top Findings</SectionTitle>
        <Typography variant="body2" sx={{ color: alpha(report.brand.ink, 0.72), mb: 1.5, lineHeight: 1.55 }}>
          The items below represent the most significant validated API findings based on severity and likely business impact.
        </Typography>
        <Stack spacing={1.15}>
          {report.topFindings.map((finding, index) => (
            <TopFindingRow key={finding.id} finding={finding} index={index} brand={report.brand} />
          ))}
        </Stack>
      </Card>
    )}

    {report.executiveSummary.length > 0 && (
      <Card
        className="exec-summary-card"
        sx={{
          p: 2.5,
          mt: 3,
          border: `1px solid ${alpha(report.brand.ink, 0.08)}`,
          boxShadow: 0,
          position: 'relative',
          overflow: 'hidden',
          bgcolor: '#fff',
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            '&::before, &::after': {
              content: '""',
              position: 'absolute',
              width: 26,
              height: 26,
              border: `2px solid ${alpha(report.brand.primary, 0.4)}`,
            },
            '&::before': { top: 12, left: 12, borderRight: 'none', borderBottom: 'none', borderRadius: '6px 0 0 0' },
            '&::after': { bottom: 12, right: 12, borderLeft: 'none', borderTop: 'none', borderRadius: '0 0 6px 0' },
          }}
        />

        <SectionTitle brand={report.brand}>Executive Summary</SectionTitle>
        <Stack direction="row" spacing={1} sx={{ mb: 1.5, color: alpha(report.brand.ink, 0.75) }}>
          <BugReportIcon sx={{ fontSize: 18, color: report.brand.primaryMid }} />
          <Typography variant="caption">Key API-layer findings and recommended actions at a glance.</Typography>
        </Stack>

        <div className="exec-summary">
          {report.executiveSummary.map((paragraph, index) => (
            <Typography
              key={`${paragraph}-${index}`}
              variant="body2"
              sx={{
                mb: 1.2,
                lineHeight: 1.65,
                whiteSpace: 'pre-wrap',
                color: report.brand.ink,
                overflowWrap: 'anywhere',
              }}
            >
              {paragraph}
            </Typography>
          ))}
        </div>

        <Divider
          sx={{
            mt: 1.5,
            borderColor: alpha(report.brand.ink, 0.08),
            '&::after': {
              content: '""',
              display: 'block',
              height: 3,
              width: 140,
              mt: 1,
              borderRadius: 2,
              background: `linear-gradient(90deg, ${report.brand.primary}, ${report.brand.primaryLight})`,
            },
          }}
        />
        <Stack direction="row" spacing={1.25} sx={{ mt: 1.25, alignItems: 'center', color: alpha(report.brand.ink, 0.75) }}>
          <TimelineIcon sx={{ fontSize: 18, color: report.brand.primaryMid }} />
          <Typography variant="caption">
            Flow: <strong>Map → Analyze → Validate → Report</strong>
          </Typography>
        </Stack>
      </Card>
    )}
  </>
);

export default ExecutiveReportAPIPentest;
